import { ReactNode, createElement } from 'react';

export type HeadingProps = {
  /** Content to be rendered within the heading element */
  children: ReactNode;
  /** Semantic heading level (h1-h6)
   * @default 1
   */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * Text alignment
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right' | 'justify';
  /** Additional CSS classes for custom styling */
  className?: string;
  /** Whether to include default bottom margin spacing
   * @default false
   */
  hasMargin?: boolean;
};

/**
 * Heading - Consistent typography scale (H1-H6)
 * Includes sensible default margins for spacing and responsive font sizes
 */
function Heading({
  children,
  level = 1,
  align,
  className = '',
  hasMargin = false,
}: Readonly<HeadingProps>) {
  const levelClasses = {
    1: 'text-3xl sm:text-4xl lg:text-5xl font-bold',
    2: 'text-2xl sm:text-3xl lg:text-4xl font-bold',
    3: 'text-xl sm:text-2xl lg:text-3xl font-semibold',
    4: 'text-lg sm:text-xl font-semibold',
    5: 'text-base sm:text-lg font-medium',
    6: 'text-sm sm:text-base font-medium',
  };

  const marginClasses = {
    1: 'mb-3 sm:mb-4',
    2: 'mb-2 sm:mb-3',
    3: 'mb-2 sm:mb-3',
    4: 'mb-1 sm:mb-2',
    5: 'mb-1 sm:mb-2',
    6: 'mb-1 sm:mb-2',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  };

  const margin = hasMargin ? marginClasses[level] : '';
  const alignment = align ? alignClasses[align] : '';

  return createElement(
    `h${level}`,
    {
      'data-slot': 'heading',
      className: `text-foreground ${levelClasses[level]} ${margin} ${alignment} ${className}`,
    },
    children
  );
}

Heading.displayName = 'Heading';

export default Heading;
